import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { useUser, getMapHomeLocation } from '@/queries/hooks/use-user'

import AsyncFetch from '@/utilities/AsyncFetch'

import { apis } from '@/config/apiConfig'

import * as utils from '@/utilities/util'

import Icon from '@/components/Icon/'
import Button from '@/components/Button'

import { useSidebarUI } from '@/contexts/sidebar-ui-context'

import { useDataConfig } from '@/contexts/DataConfigProvider'
import { useSelector, useDispatch } from 'react-redux'
import { setMapMode } from '@/actions'
import useZoomToPoint from '@/utilities/Hooks/useZoomToPoint'
import { useAnalytics, useIsFeatureEnabled } from '@/contexts/AnalyticsProvider'

import dropdownScss from '../CSSModules/Dropdown.scss'
import sidebarScss from '../CSSModules/GlobalSidebar.scss'
import tocScss from '../CSSModules/GlobalToc.scss'
import appScss from '../App/App.scss'
import scss from './Profile.scss'

export default function Sidebar({ logout }) {
  const { t } = useTranslation()
  const { data: user } = useUser()
  const { loadDataConfig } = useDataConfig()
  const mapMode = useSelector(state => state.mapMode)
  const dispatch = useDispatch()
  const { zoomToPoint } = useZoomToPoint()
  const { trackEvent } = useAnalytics()
  const { closeProfile } = useSidebarUI()
  const canAccessAccounts = useIsFeatureEnabled('access-accounts-page')
  const canAccessUsage = useIsFeatureEnabled('access-usage-page')
  const location = useLocation()

  // Store map query params when navigating to profile pages
  const [storedMapParams, setStoredMapParams] = useState(() => {
    return sessionStorage.getItem('mapQueryParams') || ''
  })

  // Keep useMemo pure - just return the appropriate params without side effects
  const currentMapParams = useMemo(() => {
    if (location.pathname === '/' && location.search) {
      return location.search
    }
    return storedMapParams
  }, [location.pathname, location.search, storedMapParams])

  // Sync sessionStorage and state when location changes
  useEffect(() => {
    if (location.pathname === '/' && location.search) {
      // Update both sessionStorage and state when on map route with search params
      sessionStorage.setItem('mapQueryParams', location.search)
      setStoredMapParams(location.search)
    } else if (location.pathname === '/' && !location.search) {
      // Clear both when on map route with no search params
      sessionStorage.removeItem('mapQueryParams')
      setStoredMapParams('')
    }
  }, [location.pathname, location.search])

  const [showOrgSwitcher, setOrgSwitcher] = useState(false)
  const [fetchingAccount, setFetchingAccount] = useState(false)
  const [fetchObjects, setFetchObjects] = useState(null)

  const [profileImgURL, setProfileImgURL] = useState(
    'https://my-asset-map-data.s3.amazonaws.com/public/client_data/static/images/user.png'
  )
  const [accountImageURLs, setAccountImageURLs] = useState([])

  const account = useMemo(
    () => utils.objectKeyVal(user?.accountID, user?.accounts, 'id') || {},
    [user]
  )

  useEffect(() => {
    async function loadProfileImage() {
      if (!user?.profile?.profileImg) return

      try {
        const imgURL = await utils.getImage(user.profile.profileImg, 'user.png')
        setProfileImgURL(imgURL)
      } catch (error) {
        console.error('Failed to load profile image:', error)
      }
    }

    loadProfileImage()
  }, [user?.profile?.profileImg])

  useEffect(() => {
    async function loadAccountImages() {
      if (!Array.isArray(user?.accounts) || user.accounts.length === 0) return

      try {
        const images = await Promise.all(
          user.accounts.map(async acc => {
            const imgURL = await utils.getImage(acc.profileImg, 'account.png')
            return {
              id: acc.id,
              name: acc.name,
              image: imgURL,
            }
          })
        )
        setAccountImageURLs(images)
      } catch (error) {
        console.error('Failed to load account images:', error)
      }
    }

    loadAccountImages()
  }, [user?.accounts])

  const handleOrgSwitcherToggle = useCallback(() => {
    const newState = !showOrgSwitcher
    setOrgSwitcher(newState)

    trackEvent('org_switcher_toggled', {
      opened: newState,
      available_accounts: user?.accounts?.length || 0,
    })
  }, [showOrgSwitcher, user?.accounts?.length, trackEvent])

  const toggleOrgSwitcher = useCallback(() => {
    if (user?.accounts?.length > 1) {
      handleOrgSwitcherToggle()
    }
  }, [user?.accounts?.length, handleOrgSwitcherToggle])

  const buildFetchParams = useCallback(accountID => {
    const method = 'POST'
    const url = `${apis.apiDatabase.uri}user/update`
    const body = { accountID }

    setFetchObjects([{ url, method, body }])
    setFetchingAccount(true)
  }, [])

  const fetchFinished = useCallback(async () => {
    loadDataConfig({
      updateStyle: false,
      resetStyle: true,
      refetchUser: true,
    }).then(result => {
      setFetchingAccount(false)

      const newHomeLocation = getMapHomeLocation(result.user)
      if (newHomeLocation) {
        zoomToPoint({
          coordinates: [newHomeLocation.y, newHomeLocation.x],
          zoom: parseFloat(newHomeLocation.z),
        })
      }
    })

    setFetchObjects(null)
  }, [loadDataConfig, zoomToPoint])

  const updateAccount = useCallback(
    accountID => {
      trackEvent('profile_account_switched', {
        old_account_id: user?.accountID,
        new_account_id: accountID,
        old_account_name: account?.name,
        available_accounts_count: user?.accounts?.length || 0,
      })

      buildFetchParams(accountID)
    },
    [
      buildFetchParams,
      user?.accountID,
      account?.name,
      user?.accounts?.length,
      trackEvent,
    ]
  )

  const backToMap = useCallback(() => {
    trackEvent('profile_back_to_map', {
      current_page: window.location.pathname,
    })

    closeProfile()
    if (mapMode === 'PRINT') {
      dispatch(setMapMode('MAP'))
    }
  }, [closeProfile, trackEvent, mapMode, dispatch])

  // Memoize back to map target to avoid multiple calls
  const backToMapTarget = useMemo(() => {
    // If we're already on the map route, just close the sidebar (no navigation needed)
    if (location.pathname === '/') {
      return null // No navigation, just close sidebar
    }

    // If we're on a profile route, navigate back to map with stored params
    return { pathname: '/', search: currentMapParams }
  }, [location.pathname, currentMapParams])

  const btnClick = useCallback(() => {
    trackEvent('profile_page_navigation', {
      current_page: window.location.pathname,
    })

    closeProfile()
  }, [closeProfile, trackEvent])

  const handleLogout = useCallback(() => {
    trackEvent('user_logout', {
      logout_from: 'profile_sidebar',
      // Note: session_duration tracking not available as loginTime is not tracked
    })

    // Clear stored map params on logout
    sessionStorage.removeItem('mapQueryParams')
    setStoredMapParams('')

    logout()
  }, [logout, trackEvent])

  // Clear stored map params when component unmounts
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('mapQueryParams')
    }
  }, [])

  return (
    <nav className={tocScss.tocProfile} onBlur={backToMap}>
      <div id={tocScss.userProfileSidebar}>
        {backToMapTarget ? (
          <NavLink to={backToMapTarget}>
            <Button
              variant='tertiary'
              iconProps={{ icon: ['far', 'angle-double-left'], size: '1x' }}
              onClick={backToMap}
              className='my-[40px] mb-[45px]'
              aria-label={t('sidebar.navigation.backToMap')}
            />
          </NavLink>
        ) : (
          <Button
            variant='tertiary'
            iconProps={{ icon: ['far', 'angle-double-left'], size: '1x' }}
            onClick={backToMap}
            className='my-[40px] mb-[45px]'
            aria-label={t('sidebar.navigation.backToMap')}
          />
        )}

        <div className={appScss['marY-md']} />

        {fetchObjects && (
          <AsyncFetch
            fetchObjects={fetchObjects}
            fetchFinished={fetchFinished}
          />
        )}

        <div className={sidebarScss.sidebarProfile}>
          <img
            alt='profile for user'
            src={profileImgURL}
            className={scss.profilePic}
          />
          <div className={sidebarScss.headingOrg}>
            <div
              className={[dropdownScss.dropdown].join(' ')}
              onClick={toggleOrgSwitcher}
            >
              {fetchingAccount ? (
                <div>{t('sidebar.account.loading')}</div>
              ) : (
                <div>{account.name}</div>
              )}

              {!fetchingAccount && user?.accounts?.length > 1 && (
                <>
                  &nbsp;&nbsp;
                  <Icon
                    icon={['fa', `chevron-${showOrgSwitcher ? 'up' : 'down'}`]}
                    size='1x'
                  />
                </>
              )}
              {/* ----------------------- START OF ORG DROPDOWN ITEMS ---------------------- */}
              {showOrgSwitcher && (
                <>
                  <div
                    style={{ maxWidth: '100%' }}
                    className={dropdownScss.dropdownCover}
                    onClick={e => {
                      e.stopPropagation()
                      toggleOrgSwitcher()
                    }}
                  />
                  <div
                    className={[
                      dropdownScss.dropdownContent,
                      dropdownScss.orgDropdownContent,
                    ].join(' ')}
                  >
                    {user?.accounts?.map(acc => {
                      const profileImgObj = utils.objectKeyVal(
                        acc.id,
                        accountImageURLs,
                        'id'
                      )

                      return (
                        <div
                          key={acc.id}
                          className={[
                            dropdownScss.dropdownItem,
                            dropdownScss.orgDropdownItem,
                          ].join(' ')}
                          onClick={() => updateAccount(acc.id)}
                        >
                          <img
                            alt='profile for account'
                            src={profileImgObj ? profileImgObj.image : ''}
                            className={[dropdownScss.orgDropdownPic].join(' ')}
                          />
                          <div className={dropdownScss.orgDropdownText}>
                            <h4>{acc.name}</h4>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
              {/* -------------------------------------------------------------------------- */}
            </div>
          </div>
          <span>
            {user?.profile?.displayName}{' '}
            {user?.isAdmin ? t('sidebar.account.adminLabel') : ''}
          </span>
        </div>

        <div className={sidebarScss.sidebarMain}>
          {fetchingAccount ? (
            <Icon icon='spinner' size='1x' fixedWidth spin />
          ) : (
            <>
              {backToMapTarget ? (
                <NavLink to={backToMapTarget}>
                  <Button
                    variant='tertiary'
                    label={t('sidebar.navigation.backToMap')}
                    onClick={backToMap}
                    className={sidebarScss.sidebarButtons}
                  />
                </NavLink>
              ) : (
                <Button
                  variant='tertiary'
                  label={t('sidebar.navigation.backToMap')}
                  onClick={backToMap}
                  className={sidebarScss.sidebarButtons}
                />
              )}
              <NavLink to='/profile'>
                <Button
                  variant='tertiary'
                  label={t('sidebar.navigation.profileAndAccount')}
                  onClick={btnClick}
                  className={sidebarScss.sidebarButtons}
                  active={location.pathname === '/profile'}
                />
              </NavLink>
              <NavLink to='/layers'>
                <Button
                  variant='tertiary'
                  label={t('sidebar.navigation.data')}
                  onClick={btnClick}
                  className={sidebarScss.sidebarButtons}
                  active={location.pathname === '/layers'}
                />
              </NavLink>
              {user?.adminAccounts?.length > 0 && (
                <>
                  <NavLink to='/maps'>
                    <Button
                      variant='tertiary'
                      label={t('sidebar.navigation.maps')}
                      onClick={btnClick}
                      className={sidebarScss.sidebarButtons}
                      active={location.pathname === '/maps'}
                    />
                  </NavLink>
                  <NavLink to='/users'>
                    <Button
                      variant='tertiary'
                      label={t('sidebar.navigation.users')}
                      onClick={btnClick}
                      className={sidebarScss.sidebarButtons}
                      active={location.pathname === '/users'}
                    />
                  </NavLink>

                  {canAccessAccounts && (
                    <NavLink to='/accounts'>
                      <Button
                        variant='tertiary'
                        label={t('sidebar.navigation.accounts')}
                        onClick={btnClick}
                        className={sidebarScss.sidebarButtons}
                        active={location.pathname === '/accounts'}
                      />
                    </NavLink>
                  )}
                  {canAccessUsage && (
                    <NavLink to='/usage'>
                      <Button
                        variant='tertiary'
                        label={t('sidebar.navigation.usage')}
                        onClick={btnClick}
                        className={sidebarScss.sidebarButtons}
                        active={location.pathname === '/usage'}
                      />
                    </NavLink>
                  )}
                </>
              )}

              <NavLink to='/' onClick={handleLogout}>
                <Button
                  variant='tertiary'
                  label={t('sidebar.navigation.signOut')}
                  className={[
                    sidebarScss.sidebarLogout,
                    sidebarScss.sidebarButtons,
                  ].join(' ')}
                />
              </NavLink>
            </>
          )}
        </div>

        <div className={sidebarScss.sidebarFooter}>
          <NavLink to='/'>
            <span className={sidebarScss.sidebarLogo}>
              {t('sidebar.brand.name')}
            </span>
          </NavLink>
          <span className='block text-[11px] text-white/50 tracking-wide mt-1'>
            {`v${process.env.VERSION}`}
            {process.env.VERCEL_GIT_COMMIT_SHA &&
            process.env.VERCEL_GIT_COMMIT_SHA !== 'N/A'
              ? ` · ${process.env.VERCEL_GIT_COMMIT_SHA.substring(0, 7)}`
              : ''}
          </span>
        </div>
      </div>
    </nav>
  )
}
